import React, { useState } from 'react';
import { ethers } from 'ethers';

interface BetBoxProps {
  marketId:     number;
  question:     string;
  yesPool:      string;  // ETH string
  noPool:       string;  // ETH string
  deadline:     number;  // unix timestamp
  resolved:     boolean;
  onPlaceBet:   (marketId: number, prediction: boolean, amountEth: string) => Promise<boolean>;
  txPending?:   boolean;
}

/**
 * BetBox
 *
 * Self-contained betting widget displaying YES/NO pool sizes, an amount
 * input, and a submit button.  Calculates implied probability and estimated
 * payout before submission.
 */
export default function BetBox({
  marketId,
  question,
  yesPool,
  noPool,
  deadline,
  resolved,
  onPlaceBet,
  txPending,
}: BetBoxProps) {
  const [prediction, setPrediction] = useState<boolean>(true);
  const [amount,     setAmount]     = useState('');
  const [feedback,   setFeedback]   = useState<string | null>(null);

  const now        = Date.now() / 1000;
  const isExpired  = deadline > 0 && now >= deadline;
  const isDisabled = resolved || isExpired || txPending;

  const yesPoolNum = parseFloat(yesPool) || 0;
  const noPoolNum  = parseFloat(noPool)  || 0;
  const totalPool  = yesPoolNum + noPoolNum;

  // Implied probability
  const yesProb = totalPool > 0 ? Math.round((yesPoolNum / totalPool) * 100) : 50;
  const noProb  = 100 - yesProb;

  // Estimated payout (parimutuel approximation, ignores fee)
  function estimatedPayout(): string {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return '—';
    const winPool  = prediction ? yesPoolNum : noPoolNum;
    const losePool = prediction ? noPoolNum  : yesPoolNum;
    const totalWin = winPool + amt;
    if (totalWin <= 0) return '—';
    const share   = (amt * losePool) / totalWin;
    return (amt + share).toFixed(4);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFeedback(null);

    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      setFeedback('Enter a valid bet amount.');
      return;
    }

    try {
      ethers.parseEther(amount); // validate format
    } catch {
      setFeedback('Invalid ETH amount format.');
      return;
    }

    const ok = await onPlaceBet(marketId, prediction, amount);
    if (ok) {
      setAmount('');
      setFeedback('✅ Bet placed successfully!');
    } else {
      setFeedback('❌ Bet failed. Check your wallet and try again.');
    }
  }

  return (
    <div className="bet-box">
      <p className="bet-box-question">{question}</p>

      {/* Pool sizes & implied probability */}
      <div className="bet-box-pools">
        <div className={`pool-side pool-yes${prediction ? ' pool-selected' : ''}`}>
          <span className="pool-label">YES</span>
          <span className="pool-prob">{yesProb}%</span>
          <span className="pool-size">{yesPoolNum.toFixed(4)} ETH</span>
        </div>
        <div className={`pool-side pool-no${!prediction ? ' pool-selected' : ''}`}>
          <span className="pool-label">NO</span>
          <span className="pool-prob">{noProb}%</span>
          <span className="pool-size">{noPoolNum.toFixed(4)} ETH</span>
        </div>
      </div>

      {/* Bet form */}
      {!resolved && !isExpired ? (
        <form onSubmit={handleSubmit} noValidate className="bet-form">
          <div className="prediction-toggle">
            <button
              type="button"
              className={`toggle-btn toggle-yes${prediction ? ' active' : ''}`}
              onClick={() => setPrediction(true)}
              disabled={isDisabled}
            >
              YES
            </button>
            <button
              type="button"
              className={`toggle-btn toggle-no${!prediction ? ' active' : ''}`}
              onClick={() => setPrediction(false)}
              disabled={isDisabled}
            >
              NO
            </button>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor={`bet-amount-${marketId}`}>
              Amount (ETH)
            </label>
            <input
              id={`bet-amount-${marketId}`}
              className="form-input"
              type="number"
              min="0"
              step="0.001"
              placeholder="0.01"
              value={amount}
              onChange={e => { setAmount(e.target.value); setFeedback(null); }}
              disabled={isDisabled}
            />
          </div>

          {amount && parseFloat(amount) > 0 && (
            <p className="bet-payout-estimate">
              Est. payout if {prediction ? 'YES' : 'NO'}: <strong>{estimatedPayout()} ETH</strong>
            </p>
          )}

          {feedback && (
            <p className={`bet-feedback ${feedback.startsWith('✅') ? 'bet-feedback-ok' : 'bet-feedback-err'}`}>
              {feedback}
            </p>
          )}

          <button
            type="submit"
            className="btn-primary btn-full"
            disabled={isDisabled || !amount}
          >
            {txPending ? '⏳ Placing bet…' : `Bet ${prediction ? 'YES' : 'NO'}`}
          </button>
        </form>
      ) : (
        <div className="bet-box-status">
          {resolved  && <span className="badge badge-expired">Market Resolved</span>}
          {isExpired && !resolved && <span className="badge badge-expired">Market Expired</span>}
        </div>
      )}
    </div>
  );
}
